import React, { useState, useRef, useEffect } from 'react';
import './VersionControl.css';
import { versionData, Version, VersionFeature, VersionIssue } from './versionData';
import { fetchMachineVersions, getMachinesForVersion, MachinesByVersion } from './machineVersions';
import { useAuth } from './AuthContext';
import Login from './Login';

const VersionControl: React.FC = () => {
  const { currentUser, isAdmin } = useAuth();

  // Load custom versions from localStorage and merge with versionData
  const loadAllVersions = () => {
    const savedVersions = localStorage.getItem('customVersions');
    const customVersions = savedVersions ? JSON.parse(savedVersions) : [];
    return [...versionData, ...customVersions];
  };

  // Reverse array so oldest is first (left)
  const reversedVersions = [...loadAllVersions()].reverse();

  const [currentIndex, setCurrentIndex] = useState(reversedVersions.length - 1); // Start at latest
  const [expandedFeatures, setExpandedFeatures] = useState<Set<string>>(new Set());
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [machinesByVersion, setMachinesByVersion] = useState<MachinesByVersion>({});
  const [isLoadingMachines, setIsLoadingMachines] = useState(true);
  const [showAddIssueForm, setShowAddIssueForm] = useState(false);
  const [newIssue, setNewIssue] = useState<{ description: string; severity: VersionIssue['severity'] }>({
    description: '',
    severity: 'medium'
  });
  // Local state to manage issues for each version (persisted in localStorage)
  const [versionIssues, setVersionIssues] = useState<Record<string, VersionIssue[]>>(() => {
    // Try to load from localStorage first
    const savedIssues = localStorage.getItem('versionIssues');
    if (savedIssues) {
      try {
        return JSON.parse(savedIssues);
      } catch (e) {
        console.error('Error parsing saved issues:', e);
      }
    }

    // Initialize with existing issues from versionData
    const initialIssues: Record<string, VersionIssue[]> = {};
    versionData.forEach(version => {
      if (version.knownIssues.length > 0) {
        // Ensure all issues have IDs
        initialIssues[version.version] = version.knownIssues.map((issue, idx) => ({
          ...issue,
          id: issue.id || `issue_${version.version}_${idx}`
        }));
      }
    });
    return initialIssues;
  });

  // State for creating new version
  const [showCreateVersionForm, setShowCreateVersionForm] = useState(false);
  const [newVersion, setNewVersion] = useState<Partial<Version>>({
    version: '',
    releaseDate: '',
    baseCommit: '',
    releaseCommit: '',
    status: 'latest',
    summary: '',
    commits: 0,
    filesChanged: 0,
    linesAdded: 0,
    linesRemoved: 0,
    majorFeatures: [],
    bugFixes: [],
    knownIssues: [],
    deployedMachines: [],
    pullRequests: [],
    breakingChanges: []
  });
  const [newFeature, setNewFeature] = useState<VersionFeature>({ title: '', description: '', details: [] });
  const [newFeatureDetail, setNewFeatureDetail] = useState('');

  const touchStartX = useRef<number>(0);
  const touchEndX = useRef<number>(0);

  const currentVersion = reversedVersions[currentIndex];
  // Get issues for current version from state
  const currentIssues = versionIssues[currentVersion.version] || [];

  // Save to localStorage whenever versionIssues changes
  useEffect(() => {
    localStorage.setItem('versionIssues', JSON.stringify(versionIssues));
  }, [versionIssues]);

  // Fetch machine versions on component mount
  useEffect(() => {
    const loadMachineVersions = async () => {
      setIsLoadingMachines(true);
      const machines = await fetchMachineVersions();
      setMachinesByVersion(machines);
      setIsLoadingMachines(false);
    };

    loadMachineVersions();
  }, []);

  const goToNext = () => {
    if (currentIndex < reversedVersions.length - 1 && !isTransitioning) {
      setIsTransitioning(true);
      setCurrentIndex(currentIndex + 1);
      setExpandedFeatures(new Set()); // Collapse all features on transition
      setIsExpanded(false); // Collapse card on transition
      setTimeout(() => setIsTransitioning(false), 500);
    }
  };

  const goToPrevious = () => {
    if (currentIndex > 0 && !isTransitioning) {
      setIsTransitioning(true);
      setCurrentIndex(currentIndex - 1);
      setExpandedFeatures(new Set());
      setIsExpanded(false); // Collapse card on transition
      setTimeout(() => setIsTransitioning(false), 500);
    }
  };

  const goToVersion = (index: number) => {
    if (index !== currentIndex && !isTransitioning) {
      setIsTransitioning(true);
      setCurrentIndex(index);
      setExpandedFeatures(new Set());
      setIsExpanded(false); // Collapse card on transition
      setTimeout(() => setIsTransitioning(false), 500);
    }
  };

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
    if (!isExpanded) {
      setExpandedFeatures(new Set()); // Reset expanded features when collapsing
    }
  };

  // Touch/swipe handlers
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    const diff = touchStartX.current - touchEndX.current;
    const threshold = 50;

    if (Math.abs(diff) > threshold) {
      if (diff > 0) {
        goToNext(); // Swipe left = next
      } else {
        goToPrevious(); // Swipe right = previous
      }
    }
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') goToNext();
      if (e.key === 'ArrowLeft') goToPrevious();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentIndex]);

  const toggleFeature = (key: string) => {
    const newExpanded = new Set(expandedFeatures);
    if (newExpanded.has(key)) {
      newExpanded.delete(key);
    } else {
      newExpanded.add(key);
    }
    setExpandedFeatures(newExpanded);
  };

  const handleAddIssue = () => {
    if (!newIssue.description.trim()) {
      alert('Please enter an issue description');
      return;
    }

    // Create new issue with unique ID (like Dashboard does)
    const newIssueId = `issue_${Date.now()}`;
    const issueToAdd: VersionIssue = {
      id: newIssueId,
      description: newIssue.description,
      severity: newIssue.severity
    };

    // Add to local state
    setVersionIssues(prev => ({
      ...prev,
      [currentVersion.version]: [...(prev[currentVersion.version] || []), issueToAdd]
    }));

    // Reset form and close
    setNewIssue({ description: '', severity: 'medium' });
    setShowAddIssueForm(false);
  };

  const cancelAddIssue = () => {
    setNewIssue({ description: '', severity: 'medium' });
    setShowAddIssueForm(false);
  };

  const handleDeleteIssue = (issueId: string) => {
    if (!window.confirm('Are you sure you want to delete this issue?')) {
      return;
    }

    // Remove from local state (like Dashboard does)
    setVersionIssues(prev => ({
      ...prev,
      [currentVersion.version]: (prev[currentVersion.version] || []).filter(issue => issue.id !== issueId)
    }));
  };

  // Handlers for creating new version
  const handleAddFeatureDetail = () => {
    if (newFeatureDetail.trim()) {
      setNewFeature(prev => ({
        ...prev,
        details: [...(prev.details || []), newFeatureDetail]
      }));
      setNewFeatureDetail('');
    }
  };

  const handleAddFeature = () => {
    if (newFeature.title && newFeature.description) {
      setNewVersion(prev => ({
        ...prev,
        majorFeatures: [...(prev.majorFeatures || []), newFeature]
      }));
      setNewFeature({ title: '', description: '', details: [] });
    }
  };

  const handleAddBugFix = (bugFix: string) => {
    if (bugFix.trim()) {
      setNewVersion(prev => ({
        ...prev,
        bugFixes: [...(prev.bugFixes || []), bugFix]
      }));
    }
  };

  const handleSaveNewVersion = () => {
    // Validate required fields
    if (!newVersion.version || !newVersion.releaseDate || !newVersion.summary) {
      alert('Please fill in all required fields: Version, Release Date, and Summary');
      return;
    }

    // Save to localStorage (add to existing versions)
    const savedVersions = localStorage.getItem('customVersions');
    const customVersions = savedVersions ? JSON.parse(savedVersions) : [];
    customVersions.push(newVersion as Version);
    localStorage.setItem('customVersions', JSON.stringify(customVersions));

    // Reset form
    setNewVersion({
      version: '',
      releaseDate: '',
      baseCommit: '',
      releaseCommit: '',
      status: 'latest',
      summary: '',
      commits: 0,
      filesChanged: 0,
      linesAdded: 0,
      linesRemoved: 0,
      majorFeatures: [],
      bugFixes: [],
      knownIssues: [],
      deployedMachines: [],
      pullRequests: [],
      breakingChanges: []
    });
    setShowCreateVersionForm(false);

    // Reload page to show new version
    window.location.reload();
  };

  const getStatusColor = (status: Version['status']): string => {
    switch (status) {
      case 'latest': return '#28a745';
      case 'stable': return '#007bff';
      case 'deprecated': return '#6c757d';
      default: return '#6c757d';
    }
  };

  const getSeverityColor = (severity: VersionIssue['severity']): string => {
    switch (severity) {
      case 'critical': return '#dc3545';
      case 'high': return '#fd7e14';
      case 'medium': return '#ffc107';
      case 'low': return '#17a2b8';
      default: return '#6c757d';
    }
  };

  const renderFeature = (feature: VersionFeature, versionId: string, featureIdx: number) => {
    const featureKey = `${versionId}-feature-${featureIdx}`;
    const isExpanded = expandedFeatures.has(featureKey);

    return (
      <div key={featureKey} className="feature-item">
        <div
          className="feature-header"
          onClick={() => feature.details && toggleFeature(featureKey)}
          style={{ cursor: feature.details ? 'pointer' : 'default' }}
        >
          <div className="feature-title-row">
            <span className="feature-bullet">•</span>
            <strong>{feature.title}</strong>
            {feature.details && (
              <span className="expand-icon">{isExpanded ? '▼' : '▶'}</span>
            )}
          </div>
          <p className="feature-description">{feature.description}</p>
        </div>

        {feature.details && isExpanded && (
          <ul className="feature-details">
            {feature.details.map((detail, idx) => (
              <li key={idx}>{detail}</li>
            ))}
          </ul>
        )}
      </div>
    );
  };

  if (!currentUser) {
    return <Login />;
  }

  return (
    <div className="version-control-container">
      <div className="version-header">
        <h1>Software Version Control</h1>
        <p className="version-subtitle">
          Track and manage software versions deployed across all machines
        </p>
      </div>

      {/* Timeline Navigation */}
      <div className="timeline-navigation">
        <div className="timeline-track">
          {reversedVersions.map((version, index) => (
            <div
              key={version.version}
              className={`timeline-dot-container ${index === currentIndex ? 'active' : ''} ${index < currentIndex ? 'completed' : ''}`}
              onClick={() => goToVersion(index)}
            >
              <div className="timeline-dot">
                {index < currentIndex && <span className="checkmark">✓</span>}
              </div>
              <span className="timeline-label">{version.version}</span>
              <span className="timeline-date">{version.releaseDate}</span>
            </div>
          ))}

          {/* Add New Version Button (Admin Only) */}
          {isAdmin && (
            <div
              className="timeline-dot-container add-version-container"
              onClick={() => setShowCreateVersionForm(true)}
              style={{ cursor: 'pointer' }}
            >
              <div className="timeline-dot add-version-dot">
                <span style={{ fontSize: '24px', color: '#6c757d' }}>+</span>
              </div>
              <span className="timeline-label" style={{ color: '#6c757d' }}>New Version</span>
            </div>
          )}
        </div>
      </div>

      {/* Version Display Area */}
      <div
        className="version-display-area"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Navigation Arrows */}
        <button
          className={`nav-arrow nav-arrow-left ${currentIndex === 0 ? 'disabled' : ''}`}
          onClick={goToPrevious}
          disabled={currentIndex === 0}
        >
          ‹
        </button>

        <button
          className={`nav-arrow nav-arrow-right ${currentIndex === reversedVersions.length - 1 ? 'disabled' : ''}`}
          onClick={goToNext}
          disabled={currentIndex === reversedVersions.length - 1}
        >
          ›
        </button>

        {/* Version Card */}
        <div className={`version-card-wrapper ${isTransitioning ? 'transitioning' : ''} ${isExpanded ? 'expanded' : ''}`}>
          <div className="version-card-main" onClick={!isExpanded ? toggleExpanded : undefined}>
            {/* Compact View - Always Visible */}
            <div className={`version-card-header-main ${!isExpanded ? 'clickable' : ''}`}>
              <div className="version-title-section">
                <h2 className="version-number">{currentVersion.version}</h2>
                <span
                  className="version-status-badge"
                  style={{ backgroundColor: getStatusColor(currentVersion.status) }}
                >
                  {currentVersion.status}
                </span>
              </div>
              <p className="version-summary">{currentVersion.summary}</p>
              <div className="version-meta-main">
                <span className="meta-item">📅 {currentVersion.releaseDate}</span>
                <span className="meta-item">📝 {currentVersion.commits} commits</span>
                <span className="meta-item">📁 {currentVersion.filesChanged} files</span>
              </div>
              {!isExpanded && (
                <div className="expand-hint">
                  <span>Click for details</span>
                  <span className="expand-arrow">▼</span>
                </div>
              )}
            </div>

            {/* Expanded Content - Shows when clicked */}
            {isExpanded && (
              <>
                {/* Collapse Button */}
                <button className="collapse-button" onClick={toggleExpanded}>
                  <span>Hide Details</span>
                  <span className="collapse-arrow">▲</span>
                </button>

                {/* Stats Grid */}
                <div className="stats-section">
              <div className="stat-box">
                <span className="stat-label">Commits</span>
                <span className="stat-value">{currentVersion.commits}</span>
              </div>
              <div className="stat-box">
                <span className="stat-label">Files</span>
                <span className="stat-value">{currentVersion.filesChanged}</span>
              </div>
              <div className="stat-box">
                <span className="stat-label">Added</span>
                <span className="stat-value added">+{currentVersion.linesAdded}</span>
              </div>
              <div className="stat-box">
                <span className="stat-label">Removed</span>
                <span className="stat-value removed">-{currentVersion.linesRemoved}</span>
              </div>
            </div>

            {/* Content Area with Scrollable Sections */}
            <div className="version-content-area">
              {/* Major Features */}
              {currentVersion.majorFeatures.length > 0 && (
                <div className="content-section">
                  <h3 className="section-title">Major Features</h3>
                  <div className="features-list">
                    {currentVersion.majorFeatures.map((feature: VersionFeature, idx: number) =>
                      renderFeature(feature, currentVersion.version, idx)
                    )}
                  </div>
                </div>
              )}

              {/* Bug Fixes */}
              {currentVersion.bugFixes.length > 0 && (
                <div className="content-section">
                  <h3 className="section-title">Bug Fixes</h3>
                  <ul className="bug-fixes-list">
                    {currentVersion.bugFixes.map((fix: string, idx: number) => (
                      <li key={idx}>
                        <span className="fix-bullet">✓</span>
                        {fix}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Known Issues */}
              {(currentIssues.length > 0 || isAdmin) && (
                <div className="content-section">
                  <div className="section-header-with-action">
                    <h3 className="section-title">Known Issues</h3>
                    {isAdmin && !showAddIssueForm && (
                      <button
                        className="add-issue-btn"
                        onClick={() => setShowAddIssueForm(true)}
                      >
                        + Add Issue
                      </button>
                    )}
                  </div>

                  {showAddIssueForm && (
                    <div className="add-issue-form">
                      <div className="form-group">
                        <label>Severity:</label>
                        <select
                          value={newIssue.severity}
                          onChange={(e) => setNewIssue({ ...newIssue, severity: e.target.value as VersionIssue['severity'] })}
                          className="severity-select"
                        >
                          <option value="low">Low</option>
                          <option value="medium">Medium</option>
                          <option value="high">High</option>
                          <option value="critical">Critical</option>
                        </select>
                      </div>
                      <div className="form-group">
                        <label>Description:</label>
                        <textarea
                          value={newIssue.description}
                          onChange={(e) => setNewIssue({ ...newIssue, description: e.target.value })}
                          placeholder="Describe the issue..."
                          className="issue-textarea"
                          rows={3}
                        />
                      </div>
                      <div className="form-actions">
                        <button
                          className="btn-save"
                          onClick={handleAddIssue}
                        >
                          Save Issue
                        </button>
                        <button
                          className="btn-cancel"
                          onClick={cancelAddIssue}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}

                  {currentIssues.length > 0 ? (
                    <div className="issues-list">
                      {currentIssues.map((issue, idx) => (
                        <div key={issue.id || idx} className="issue-item">
                          <span
                            className="severity-badge"
                            style={{ backgroundColor: getSeverityColor(issue.severity) }}
                          >
                            {issue.severity}
                          </span>
                          <span className="issue-description">{issue.description}</span>
                          {isAdmin && issue.id && (
                            <button
                              className="delete-issue-btn"
                              onClick={() => handleDeleteIssue(issue.id!)}
                              title="Delete issue"
                            >
                              ✕
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : !showAddIssueForm && (
                    <p className="no-data">No known issues for this version</p>
                  )}
                </div>
              )}

              {/* Deployed Machines */}
              <div className="content-section">
                <h3 className="section-title">Deployed Machines</h3>
                {isLoadingMachines ? (
                  <p className="loading-text">Loading machines...</p>
                ) : (() => {
                  const deployedMachines = getMachinesForVersion(machinesByVersion, currentVersion.version);

                  return deployedMachines.length > 0 ? (
                    <>
                      <div className="machines-count">
                        <span className="count-badge">{deployedMachines.length} machine{deployedMachines.length !== 1 ? 's' : ''}</span>
                      </div>
                      <div className="machines-list">
                        {deployedMachines.sort().map((machine, idx) => (
                          <span key={idx} className="machine-badge">
                            {machine}
                          </span>
                        ))}
                      </div>
                    </>
                  ) : (
                    <p className="no-data">No machines currently deployed with this version</p>
                  );
                })()}
              </div>

              {/* Git References */}
              <div className="content-section">
                <h3 className="section-title">Git References</h3>
                <div className="git-refs">
                  <div className="git-ref-item">
                    <span className="ref-label">Base:</span>
                    <code className="ref-value">{currentVersion.baseCommit}</code>
                  </div>
                  <div className="git-ref-item">
                    <span className="ref-label">Release:</span>
                    <code className="ref-value">{currentVersion.releaseCommit}</code>
                  </div>
                  {currentVersion.pullRequests && currentVersion.pullRequests.length > 0 && (
                    <div className="git-ref-item">
                      <span className="ref-label">PRs:</span>
                      <span className="ref-value-text">{currentVersion.pullRequests.join(', ')}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
              </>
            )}
          </div>
        </div>

        {/* Progress Indicator */}
        <div className="progress-indicator">
          <span className="progress-text">
            {currentIndex + 1} / {reversedVersions.length}
          </span>
          <div className="progress-dots">
            {reversedVersions.map((_, index) => (
              <button
                key={index}
                className={`progress-dot ${index === currentIndex ? 'active' : ''}`}
                onClick={() => goToVersion(index)}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Navigation Hint */}
      <div className="navigation-hint">
        <span>Use arrow keys ← → or swipe to navigate</span>
      </div>

      {/* Create New Version Modal */}
      {showCreateVersionForm && isAdmin && (
        <div className="modal-overlay" onClick={() => setShowCreateVersionForm(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '800px', maxHeight: '90vh', overflowY: 'auto' }}>
            <h2 style={{ marginBottom: '20px', color: '#2c3e50' }}>Create New Version</h2>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
              {/* Basic Info */}
              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>Version Number *</label>
                <input
                  type="text"
                  placeholder="e.g., v1.2.3"
                  value={newVersion.version}
                  onChange={(e) => setNewVersion({ ...newVersion, version: e.target.value })}
                  style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>Release Date *</label>
                <input
                  type="text"
                  placeholder="e.g., January 16, 2026"
                  value={newVersion.releaseDate}
                  onChange={(e) => setNewVersion({ ...newVersion, releaseDate: e.target.value })}
                  style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>Summary *</label>
                <textarea
                  placeholder="Brief summary of this release..."
                  value={newVersion.summary}
                  onChange={(e) => setNewVersion({ ...newVersion, summary: e.target.value })}
                  style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc', minHeight: '60px' }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>Status</label>
                  <select
                    value={newVersion.status}
                    onChange={(e) => setNewVersion({ ...newVersion, status: e.target.value as Version['status'] })}
                    style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                  >
                    <option value="latest">Latest</option>
                    <option value="stable">Stable</option>
                    <option value="deprecated">Deprecated</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>Commits</label>
                  <input
                    type="number"
                    value={newVersion.commits}
                    onChange={(e) => setNewVersion({ ...newVersion, commits: parseInt(e.target.value) || 0 })}
                    style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>Files Changed</label>
                  <input
                    type="number"
                    value={newVersion.filesChanged}
                    onChange={(e) => setNewVersion({ ...newVersion, filesChanged: parseInt(e.target.value) || 0 })}
                    style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>Lines Added</label>
                  <input
                    type="number"
                    value={newVersion.linesAdded}
                    onChange={(e) => setNewVersion({ ...newVersion, linesAdded: parseInt(e.target.value) || 0 })}
                    style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>Lines Removed</label>
                  <input
                    type="number"
                    value={newVersion.linesRemoved}
                    onChange={(e) => setNewVersion({ ...newVersion, linesRemoved: parseInt(e.target.value) || 0 })}
                    style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                  />
                </div>
              </div>

              {/* Git Commit Info */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>Base Commit</label>
                  <input
                    type="text"
                    placeholder="e.g., abc123"
                    value={newVersion.baseCommit}
                    onChange={(e) => setNewVersion({ ...newVersion, baseCommit: e.target.value })}
                    style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>Release Commit</label>
                  <input
                    type="text"
                    placeholder="e.g., def456"
                    value={newVersion.releaseCommit}
                    onChange={(e) => setNewVersion({ ...newVersion, releaseCommit: e.target.value })}
                    style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                  />
                </div>
              </div>

              {/* Major Features Section */}
              <div style={{ marginTop: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                <h3 style={{ marginBottom: '15px', color: '#2c3e50' }}>Major Features</h3>
                {newVersion.majorFeatures && newVersion.majorFeatures.length > 0 && (
                  <div style={{ marginBottom: '15px' }}>
                    {newVersion.majorFeatures.map((feature, idx) => (
                      <div key={idx} style={{ marginBottom: '10px', padding: '10px', backgroundColor: 'white', borderRadius: '4px' }}>
                        <strong>{feature.title}</strong>: {feature.description}
                        {feature.details && feature.details.length > 0 && (
                          <ul style={{ marginTop: '5px', paddingLeft: '20px' }}>
                            {feature.details.map((detail, dIdx) => (
                              <li key={dIdx}>{detail}</li>
                            ))}
                          </ul>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                <div>
                  <input
                    type="text"
                    placeholder="Feature title"
                    value={newFeature.title}
                    onChange={(e) => setNewFeature({ ...newFeature, title: e.target.value })}
                    style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc', marginBottom: '8px' }}
                  />
                  <textarea
                    placeholder="Feature description"
                    value={newFeature.description}
                    onChange={(e) => setNewFeature({ ...newFeature, description: e.target.value })}
                    style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc', minHeight: '50px', marginBottom: '8px' }}
                  />
                  <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                    <input
                      type="text"
                      placeholder="Add detail (optional)"
                      value={newFeatureDetail}
                      onChange={(e) => setNewFeatureDetail(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleAddFeatureDetail()}
                      style={{ flex: 1, padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                    />
                    <button onClick={handleAddFeatureDetail} style={{ padding: '8px 15px', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                      Add Detail
                    </button>
                  </div>
                  {newFeature.details && newFeature.details.length > 0 && (
                    <ul style={{ marginBottom: '8px', paddingLeft: '20px' }}>
                      {newFeature.details.map((detail, idx) => (
                        <li key={idx}>{detail}</li>
                      ))}
                    </ul>
                  )}
                  <button onClick={handleAddFeature} style={{ padding: '8px 15px', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                    Add Feature
                  </button>
                </div>
              </div>

              {/* Bug Fixes Section */}
              <div style={{ marginTop: '15px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                <h3 style={{ marginBottom: '15px', color: '#2c3e50' }}>Bug Fixes</h3>
                {newVersion.bugFixes && newVersion.bugFixes.length > 0 && (
                  <ul style={{ marginBottom: '10px', paddingLeft: '20px' }}>
                    {newVersion.bugFixes.map((fix, idx) => (
                      <li key={idx}>{fix}</li>
                    ))}
                  </ul>
                )}
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input
                    type="text"
                    placeholder="Describe bug fix..."
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        const target = e.target as HTMLInputElement;
                        handleAddBugFix(target.value);
                        target.value = '';
                      }
                    }}
                    style={{ flex: 1, padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                  />
                  <button
                    onClick={(e) => {
                      const input = (e.currentTarget.previousElementSibling as HTMLInputElement);
                      handleAddBugFix(input.value);
                      input.value = '';
                    }}
                    style={{ padding: '8px 15px', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                  >
                    Add Bug Fix
                  </button>
                </div>
              </div>

              {/* Action Buttons */}
              <div style={{ display: 'flex', gap: '10px', marginTop: '20px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => setShowCreateVersionForm(false)}
                  style={{ padding: '10px 20px', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer' }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveNewVersion}
                  style={{ padding: '10px 20px', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer' }}
                >
                  Save Version
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VersionControl;
